import React, { useState } from "react";
import { Form } from "react-admin";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useNotifyError } from "utils/useNotifyError";
import ModalTitle, { getTriggerIcon, modalCancelButtonSx, modalConfirmButtonSx, modalContentSx, withModalSx } from "./ModalTitle";

const FormModal = ({
  onConfirm,
  onOpen,
  titleContent,
  // Header extras: icon badge (defaults to the trigger's icon) and a secondary line under the title.
  titleIcon,
  subtitle,
  children,
  startIcon,
  btnStyle,
  defaultValues,
  titleButton,
  triggerButton,
  saveTitle = "Потвърди",
  saveIcon = <SaveIcon />,
  closeTitle = "Отказ",
  size = "medium",
  dialogSize = "sm",
  confirmEnabled = true,
  hideSaveButton = false,
  dialogProps,
  resetProps = null,
  variant = "contained",
  disableBackdropClose = false,
  mode = "onSubmit",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const notifyError = useNotifyError();

  const handleOpen = async () => {
    if (!onOpen) {
      setIsOpen(true);
      return;
    }

    const shouldOpen = await onOpen();

    if (shouldOpen !== false) {
      setIsOpen(true);
    }
  };

  const handleClose = disableBackdropClose => {
    !loading && !disableBackdropClose && setIsOpen(false);
    if (resetProps) resetProps();
  }; // Prevent closing while loading

  const handleConfirm = async data => {
    if (loading) return;
    setLoading(true);
    try {
      const result = await onConfirm(data);

      if (result !== false) {
        setIsOpen(false);
      }
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  };

  const trigger = triggerButton ? (
    React.cloneElement(triggerButton, { onClick: handleOpen })
  ) : (
    <Button
      sx={btnStyle}
      variant={variant}
      size={size}
      onClick={handleOpen}
      startIcon={startIcon || <ThumbUpIcon />}
    >
      {titleButton}
    </Button>
  );

  return (
    <>
      {trigger}
      <Dialog
        open={isOpen}
        onClose={() => handleClose(disableBackdropClose)}
        fullWidth
        maxWidth={dialogSize}
        {...dialogProps}
        sx={withModalSx(
          {
            // The react-admin <Form> renders a <form> element between the Dialog paper and
            // its content. Make it a flex column that fills the paper so DialogContent scrolls
            // on overflow while DialogTitle / DialogActions stay fixed (same as ConfirmModal).
            "& form": {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            },
          },
          dialogProps?.sx,
        )}
      >
        <ModalTitle
          icon={titleIcon ?? (triggerButton ? getTriggerIcon(triggerButton) : startIcon)}
          title={titleContent}
          subtitle={subtitle}
        />
        <Form
          onSubmit={handleConfirm}
          defaultValues={defaultValues}
          mode={mode}
        >
          {children && (
            <>
              <DialogContent
                dividers
                sx={modalContentSx}
              >
                {children}
              </DialogContent>

              <DialogActions sx={{ display: "flex", justifyContent: "space-between", px: 3, py: 2 }}>
                <Button
                  onClick={() => handleClose(false)}
                  size="medium"
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  sx={modalCancelButtonSx}
                >
                  {closeTitle}
                </Button>
                {!hideSaveButton ? (
                  <Button
                    type="submit"
                    variant="contained"
                    size="medium"
                    startIcon={saveIcon}
                    disabled={!confirmEnabled || loading}
                    sx={modalConfirmButtonSx}
                  >
                    {saveTitle}
                  </Button>
                ) : null}
              </DialogActions>
            </>
          )}
        </Form>
      </Dialog>
    </>
  );
};

export default FormModal;
